/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, Redirect } from "react-router-dom";

import {
  ActionButton,
  CommandBar,
  ICommandBarItemProps,
  Stack,
  StackItem,
  Text,
} from "@fluentui/react";

import { useAuth } from "Authentication/AuthProvider";
import {
  bodyContainerStackStyles,
  headerStackStyles,
  headerTextStyles,
  ContainerWrapper,
} from "Components/Common/SharedTableStyles.styles";
import { OperationStatus } from "Constants/OperationStatus";
import { PermissionsEnum } from "Constants/RolesPermissionsConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { approveOrDeclineAzureBenefitRequest } from "Dataflows/AzureBenefitRequests/AzureBenefitRequestsAction";
import {
  getApproveOrDeclineRequestErrorMessage,
  getApproveOrDeclineRequestOperationStatus,
  getAzureBenefitRequests,
  getFetchAzureBenefitRequestDetailsErrorMessage,
  getSelectedAzureBenefitRequestDetails,
  getSelectedAzureBenefitRequestExpeditedByDate,
  getSelectedAzureBenefitRequestIsExpedited,
} from "Dataflows/AzureBenefitRequests/AzureBenefitRequestsSelector";
import { formatDate } from "Utility/FormatHelper";

import {
  answerTextStyles,
  bodyContainerExpeditedStackStyles,
  bodyContainerStackTokens,
  commandBarStyles,
  containerStackTokens,
  emailStackTokens,
  emailTextStyles,
  errorMessageStyles,
  mainContainerStyles,
  nameInfoStackStyles,
  nameTextStyles,
  questionTextStyles,
  wrapperStackStyles,
} from "./AzureBenefitRequestDetailsContainer.styles";

/**
 * The Azure Benefit Request Details Container component
 * @method
 * @returns {ReactElement} The Azure Benefit Request Details Container component
 */
const AzureBenefitRequestDetailsContainer = (): ReactElement => {
  const { isUserLoggedIn, loading, hasAnyRoles, hasPermission } = useAuth();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id, tenant } = useParams<{ id: string; tenant: string }>();
  const [selectedUser, setSelectedUser] = useState({
    firstName: "",
    lastName: "",
    siteEmail: "",
    id: 0,
  });

  const azureBenefitRequestDetails = useSelector(getSelectedAzureBenefitRequestDetails);
  const azureBenefitRequests = useSelector(getAzureBenefitRequests);
  const azureBenefitRequestDetailsError = useSelector(
    getFetchAzureBenefitRequestDetailsErrorMessage
  );
  const approveOrDeclineOperationStatus = useSelector(getApproveOrDeclineRequestOperationStatus);
  const approveOrDeclineError = useSelector(getApproveOrDeclineRequestErrorMessage);
  const azureBenefitRequestExpeditedByDate = useSelector(
    getSelectedAzureBenefitRequestExpeditedByDate
  );
  const azureBenefitRequestIsExpedited = useSelector(getSelectedAzureBenefitRequestIsExpedited);
  const canUpdateBenefit =
    (tenant.toLowerCase() === TenantTypesEnum.MVP.toLowerCase() &&
      hasPermission(TenantTypesEnum.MVP, PermissionsEnum.MVP_UPDATE_BENEFITS)) ||
    (tenant.toLowerCase() === TenantTypesEnum.RD.toLowerCase() &&
      hasPermission(TenantTypesEnum.RD, PermissionsEnum.RD_UPDATE_BENEFITS));

  if (isUserLoggedIn && !hasAnyRoles && !loading) {
    return <Redirect to="/unauthorized" />;
  }

  useEffect(() => {
    if (azureBenefitRequests.length > 0 && id) {
      const user = azureBenefitRequests.filter((req) => req.userProfileId === Number(id))[0];
      setSelectedUser(user);
    }
  }, [azureBenefitRequests]);

  useEffect(() => {
    if (approveOrDeclineOperationStatus === OperationStatus.Succeeded) {
      history.push(`/${tenant}/benefits/azureRequests`);
    }
  }, [approveOrDeclineOperationStatus]);

  /**
   * Handler for when the back button is clicked.
   * @returns {void} void no return for function.
   */
  const onBackClickEvent = (): void => {
    history.goBack();
  };

  const onApproveClicked = (): void => {
    dispatch(approveOrDeclineAzureBenefitRequest(true, selectedUser.id, tenant));
  };

  const onDeclineClicked = (): void => {
    dispatch(approveOrDeclineAzureBenefitRequest(false, selectedUser.id, tenant));
  };

  const items: ICommandBarItemProps[] = [
    {
      key: "approveRequest",
      text: "Approve",
      iconProps: { iconName: "Accept" },
      onClick: (): void => {
        onApproveClicked();
      },
    },
    {
      key: "declineRequest",
      text: "Decline",
      iconProps: { iconName: "Cancel" },
      onClick: (): void => {
        onDeclineClicked();
      },
    },
  ];

  if (azureBenefitRequestDetailsError !== "") {
    return <div>{azureBenefitRequestDetailsError}</div>;
  }

  return (
    <Stack styles={wrapperStackStyles}>
      <Stack>
        <ActionButton
          iconProps={{ iconName: "RevToggleKey" }}
          ariaLabel="Back to Search Items"
          onClick={onBackClickEvent}
        >
          Back to Search Items
        </ActionButton>
        <Stack tokens={containerStackTokens}>
          <Stack styles={nameInfoStackStyles}>
            <StackItem>
              <Text styles={nameTextStyles}>
                {`${selectedUser?.firstName} ${selectedUser?.lastName}`}
              </Text>
            </StackItem>
            <Stack horizontal tokens={emailStackTokens}>
              <StackItem>
                <Text styles={emailTextStyles}>{`${selectedUser?.siteEmail}`}</Text>
              </StackItem>
            </Stack>
          </Stack>
          <Stack styles={mainContainerStyles}>
            {canUpdateBenefit && (
              <StackItem>
                <CommandBar
                  items={items}
                  ariaLabel="Azure Benefit Request actions"
                  styles={commandBarStyles}
                />
              </StackItem>
            )}
            {approveOrDeclineError !== "" && (
              <StackItem styles={errorMessageStyles}>
                <p>Failed to approve or decline user.</p>
              </StackItem>
            )}
            <ContainerWrapper>
              <Stack styles={headerStackStyles}>
                <Text styles={headerTextStyles}>Azure Offer Questionnaire</Text>
              </Stack>
              <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
                {azureBenefitRequestDetails &&
                  Object.keys(azureBenefitRequestDetails).map((q) => (
                    <>
                      <div
                        key={`questionnaire-question-${Object.keys(
                          azureBenefitRequestDetails
                        ).indexOf(q)}`}
                        style={{ margin: 0 }}
                      >
                        <Text styles={questionTextStyles}>{q}</Text>
                        <Text styles={answerTextStyles}>{azureBenefitRequestDetails[q]}</Text>
                      </div>
                    </>
                  ))}
              </Stack>
              <Stack styles={bodyContainerExpeditedStackStyles} tokens={bodyContainerStackTokens}>
                <div>
                  <Text styles={questionTextStyles}>Do you need this request expedited?</Text>
                  <Text styles={answerTextStyles}>
                    {azureBenefitRequestIsExpedited ? "Yes" : "No"}
                  </Text>
                  {azureBenefitRequestExpeditedByDate && (
                    <Text styles={answerTextStyles}>
                      {formatDate(azureBenefitRequestExpeditedByDate)}
                    </Text>
                  )}
                </div>
              </Stack>
            </ContainerWrapper>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default AzureBenefitRequestDetailsContainer;
